import { Box, Card, CardContent, Typography } from '@mui/material'
import type { Feature } from 'geojson'
import { useMapContext } from './MapContext'
import { getColor } from './utils'

export interface LegendProps {
  layers: Feature[]
}

export const Legend: React.FC<LegendProps> = ({ layers }) => {
  const { legendDirection, mapWidth, perimeter } = useMapContext()
  const allLayers = [...layers.flat(), perimeter].filter(Boolean)

  const layerTypeMap: Record<string, string> = {
    strata: 'Estrato',
    exclusion: 'Área de exclusión',
    exclusion_area: 'Área de exclusión',
    site: 'Sitio de monitoreo',
    perimeter: 'Perímetro',
    wetlands: 'Área no elegible',
    forest: 'Área no elegible',
    deforestedAreas: 'Área no elegible',
    forestAndRed: 'Área no elegible',
    carbon_instance: 'Área de programa',
    paddock: 'Lote',
    default: 'Otro',
  }

  const uniqueLayers = allLayers.filter((layer, index, self) => {
    const { properties } = layer
    const typeLabel = layerTypeMap[properties.type] || layerTypeMap.default
    const color = getColor(layer)
    const { name } = properties

    return (
      index ===
      self.findIndex(l => {
        const lTypeLabel = layerTypeMap[l.properties.type] || layerTypeMap.default
        return lTypeLabel === typeLabel && l.properties.name === name && getColor(l) === color
      })
    )
  })

  return (
    <Card
      elevation={0}
      sx={{
        maxWidth: mapWidth,
        backgroundColor: '#ffffff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: legendDirection === 'row' && uniqueLayers.length > 1 ? '1fr 1fr' : '1fr',
            },
            columnGap: 4,
            rowGap: 1.2,
          }}
        >
          {uniqueLayers.map(layer => {
            const { properties } = layer
            const typeLabel = layerTypeMap[properties.type] || layerTypeMap.default

            return (
              <Box
                key={`${properties.type}-${properties.name}-${properties.id}`}
                sx={{
                  display: 'flex',
                  gap: '10px',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: getColor(layer),
                    width: '14px',
                    height: '14px',
                    minWidth: '14px',
                    borderRadius: '2px',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem' }} variant="body2">
                  <b style={{ color: 'rgba(0,0,0,0.85)' }}>{typeLabel}</b>:{' '}
                  <span style={{ color: 'rgba(0,0,0,0.65)' }}>{properties.name}</span>
                </Typography>
              </Box>
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}
